/**
 * Passcode gate — the bahi-khaata cover. Single quote, date stamp,
 * the कलम waits for the सेठ.
 */
import { store } from '../store.ts';
import { decryptJSON } from '../crypto.ts';
import { saveSummary, loadSummary } from '../storage.ts';
import type { Summary } from '../types.ts';

const TODAY = new Date();
const ISSUE_DATE = TODAY.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }).toUpperCase();

const TPL = /* html */ `
<style>
:host {
  display: grid;
  place-items: center;
  inset: 0;
  background:
    radial-gradient(ellipse 90% 60% at 50% -10%, oklch(96% 0.020 80), transparent 65%),
    radial-gradient(ellipse 120% 80% at 50% 110%, oklch(82% 0.045 70 / 0.6), transparent 60%),
    var(--paper);
  position: relative;
  overflow: hidden;
}
/* ledger rule grid baked in for the cover */
:host::before {
  content: '';
  position: absolute;
  inset: 0;
  pointer-events: none;
  background:
    repeating-linear-gradient(
      to bottom,
      transparent 0 27px,
      oklch(60% 0.040 50 / 0.075) 27px 28px
    );
  opacity: 0.55;
}
:host::after {
  content: '';
  position: absolute;
  inset: 0;
  pointer-events: none;
  opacity: 0.3;
  mix-blend-mode: multiply;
  background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='320' height='320'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.72' numOctaves='2' stitchTiles='stitch'/><feColorMatrix values='0 0 0 0 0.55  0 0 0 0 0.42  0 0 0 0 0.28  0 0 0 0.38 0'/></filter><rect width='100%' height='100%' filter='url(%23n)'/></svg>");
}
.cover {
  position: relative;
  z-index: 2;
  display: grid;
  gap: 14px;
  padding: 40px 32px;
  max-width: 460px;
  width: min(460px, 92vw);
  text-align: center;
}
.top-rule {
  display: grid;
  grid-template-columns: auto 1fr auto;
  align-items: center;
  gap: 14px;
  font-family: var(--mono);
  font-size: 9px;
  letter-spacing: 0.36em;
  text-transform: uppercase;
  color: var(--ink-dim);
  padding-bottom: 16px;
  border-bottom: 1px solid var(--ink);
  margin-bottom: 4px;
}
.top-rule .center {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}
.top-rule .dot {
  width: 4px;
  height: 4px;
  background: var(--crimson);
  border-radius: 50%;
  display: inline-block;
}
.mark {
  font-family: var(--hindi);
  font-size: clamp(110px, 28vw, 184px);
  font-weight: 400;
  color: var(--crimson);
  line-height: 0.85;
  letter-spacing: -0.02em;
  text-shadow:
    0 1px 0 oklch(38% 0.175 22 / 0.18),
    0 0 24px oklch(46% 0.180 25 / 0.10);
  margin-top: 8px;
}
.en {
  font-family: var(--display);
  font-style: italic;
  font-weight: 600;
  font-size: clamp(28px, 8vw, 40px);
  color: var(--ink);
  margin-top: -4px;
  letter-spacing: 0.01em;
}
.en::before, .en::after {
  content: '·';
  color: var(--crimson);
  margin: 0 12px;
  opacity: 0.7;
}
.quote {
  font-family: var(--display);
  font-size: 19px;
  font-style: italic;
  color: var(--ink);
  font-weight: 500;
  margin: 22px 0 6px;
  line-height: 1.4;
  text-wrap: balance;
  position: relative;
  padding: 0 14px;
}
.quote::before {
  content: '\\201C';
  font-family: var(--display);
  font-style: italic;
  font-size: 56px;
  font-weight: 700;
  color: var(--crimson);
  line-height: 0;
  vertical-align: -0.4em;
  margin-right: 4px;
  opacity: 0.7;
}
.quote::after {
  content: '\\201D';
  font-family: var(--display);
  font-style: italic;
  font-size: 56px;
  font-weight: 700;
  color: var(--crimson);
  line-height: 0;
  vertical-align: -0.4em;
  margin-left: 4px;
  opacity: 0.7;
}
.attribution {
  font-family: var(--mono);
  font-size: 9.5px;
  letter-spacing: 0.32em;
  text-transform: uppercase;
  color: var(--ink-faint);
  margin-bottom: 22px;
}
.rule-pair {
  display: flex;
  flex-direction: column;
  gap: 3px;
  align-items: center;
  margin: 14px 0 18px;
}
.rule-pair span {
  height: 1px;
  background: var(--ink);
  display: block;
}
.rule-pair span:nth-child(1) { width: 120px; opacity: 0.85; }
.rule-pair span:nth-child(2) { width: 80px; opacity: 0.55; background: var(--crimson); }
.field {
  display: grid;
  gap: 10px;
  margin-top: 4px;
}
input {
  font-family: var(--mono);
  font-size: 18px;
  text-align: center;
  padding: 18px 16px;
  background: oklch(96% 0.018 78);
  border: 1px solid var(--rule-strong);
  border-radius: 2px;
  color: var(--ink);
  outline: none;
  letter-spacing: 0.4em;
  transition: border-color 200ms cubic-bezier(0.32, 0.72, 0, 1);
}
input:focus {
  border-color: var(--crimson);
  box-shadow: 0 0 0 3px var(--crimson-wash);
}
input::placeholder { color: var(--ink-faint); letter-spacing: 0.2em; }
button {
  font-family: var(--mono);
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.42em;
  padding: 18px;
  background: var(--crimson);
  color: var(--paper);
  border-radius: 2px;
  font-weight: 700;
  transition: transform 120ms cubic-bezier(0.32, 0.72, 0, 1), filter 120ms, box-shadow 200ms;
  position: relative;
  overflow: hidden;
}
button::before {
  content: '';
  position: absolute;
  inset: 1px;
  border: 1px solid oklch(94% 0.022 78 / 0.4);
  border-radius: inherit;
  pointer-events: none;
}
button:hover { filter: brightness(1.06); box-shadow: 0 6px 18px -6px var(--crimson-wash); }
button:active { transform: scale(0.97); filter: brightness(0.94); }
.hint {
  font-family: var(--mono);
  font-size: 9.5px;
  color: var(--ink-faint);
  margin-top: 6px;
  letter-spacing: 0.18em;
  text-transform: uppercase;
}
.err {
  font-family: var(--mono);
  font-size: 11px;
  color: var(--crimson);
  letter-spacing: 0.06em;
  min-height: 14px;
  font-weight: 700;
}
.bottom-rule {
  margin-top: 28px;
  padding-top: 16px;
  border-top: 1px solid var(--ink);
  display: flex;
  justify-content: space-between;
  font-family: var(--mono);
  font-size: 8.5px;
  letter-spacing: 0.32em;
  text-transform: uppercase;
  color: var(--ink-faint);
}
.bottom-rule .crimson {
  color: var(--crimson);
  font-weight: 700;
}
</style>
<section class="cover">
  <div class="top-rule">
    <span>Vol. I</span>
    <span class="center">हिसाब<span class="dot"></span>The Ledger<span class="dot"></span>Issue I</span>
    <span>${ISSUE_DATE}</span>
  </div>
  <div>
    <div class="mark">हिसाब</div>
    <div class="en">Hisaab</div>
  </div>
  <div class="quote">A man without a ledger is a shop without a door.</div>
  <div class="attribution">— बही-खाता proverb</div>
  <div class="rule-pair"><span></span><span></span></div>
  <form class="field" id="form">
    <input id="pass" type="password" placeholder="passcode" autocomplete="off" autocapitalize="off" autocorrect="off" inputmode="text" />
    <button type="submit" id="btn">Open the book</button>
    <div class="err" id="err"></div>
  </form>
  <div class="hint">on-device · encrypted · nothing leaves this page</div>
  <div class="bottom-rule">
    <span>MMXXVI</span>
    <span class="crimson">Issue I</span>
  </div>
</section>
`;

export class HisaabGate extends HTMLElement {
  #shadow = this.attachShadow({ mode: 'open' });

  connectedCallback(): void {
    this.#shadow.innerHTML = TPL;
    const form = this.#shadow.getElementById('form') as HTMLFormElement;
    const pass = this.#shadow.getElementById('pass') as HTMLInputElement;
    const err = this.#shadow.getElementById('err') as HTMLDivElement;

    queueMicrotask(() => pass.focus());

    form.addEventListener('submit', async (e) => {
      e.preventDefault();
      err.textContent = '';
      const passcode = pass.value.trim();
      if (!passcode) return;
      try {
        const summary = await this.#unlock(passcode);
        await saveSummary(summary);
        sessionStorage.setItem('hisaab:passcode', passcode);
        this.#transitionToApp(summary);
      } catch (e2) {
        err.textContent = 'galat passcode · try again';
        pass.value = '';
        pass.focus();
      }
    });
  }

  async #unlock(passcode: string): Promise<Summary> {
    const cached = await loadSummary();
    if (cached) return cached;
    const res = await fetch('/data.encrypted.json', { cache: 'no-cache' });
    if (!res.ok) throw new Error(`fetch failed: ${res.status}`);
    const body = await res.json() as { ciphertext: string };
    return decryptJSON<Summary>(passcode, body.ciphertext);
  }

  #transitionToApp(summary: Summary): void {
    const apply = (): void => {
      store.patch({ phase: 'ready', summary, error: null });
    };
    if ('startViewTransition' in document) {
      (document as Document & { startViewTransition: (cb: () => void) => unknown }).startViewTransition(apply);
    } else {
      apply();
    }
  }
}

customElements.define('hisaab-gate', HisaabGate);
